import { createTheme, Theme } from '@mui/material/styles'

// Material Design 3 Color System - Harmonious & Vibrant Palette
export const material3Colors = {
  // Primary Color Palette - Dynamic Blue
  primary: {
    0: '#000000',
    10: '#001d36',
    20: '#003258',
    25: '#003f6b',
    30: '#004c7e',
    35: '#005a92',
    40: '#0069a6',
    50: '#0086d0',
    60: '#00a4fc',
    70: '#4fc3ff',
    80: '#7dd5ff',
    90: '#c2e7ff',
    95: '#e1f3ff',
    98: '#f8fdff',
    99: '#fcfeff',
    100: '#ffffff',
  },

  // Secondary Color Palette - Harmonious Teal
  secondary: {
    0: '#000000',
    10: '#0a1f1c',
    20: '#1f3530',
    25: '#2a403b',
    30: '#354c47',
    35: '#415853',
    40: '#4d645f',
    50: '#667d77',
    60: '#7f9691',
    70: '#99b1ab',
    80: '#b4ccc6',
    90: '#d0e8e1',
    95: '#def6ef',
    98: '#f0fdf9',
    99: '#f7fefb',
    100: '#ffffff',
  },

  // Tertiary Color Palette - Vibrant Purple
  tertiary: {
    0: '#000000',
    10: '#2d1b69',
    20: '#44307e',
    25: '#50398a',
    30: '#5d4397',
    35: '#6a4ea4',
    40: '#7759b1',
    50: '#9270cd',
    60: '#ae88ea',
    70: '#c9a2ff',
    80: '#ddbdff',
    90: '#f0d9ff',
    95: '#f8ecff',
    98: '#fef7ff',
    99: '#fffbff',
    100: '#ffffff',
  },

  // Error Color Palette
  error: {
    0: '#000000',
    10: '#410e0b',
    20: '#601410',
    25: '#6f1913',
    30: '#7d1f16',
    35: '#8c2619',
    40: '#9b2d1c',
    50: '#ba1a1a',
    60: '#de3730',
    70: '#ff5449',
    80: '#ff897d',
    90: '#ffb4ab',
    95: '#ffcdc7',
    98: '#fff8f7',
    99: '#fffbff',
    100: '#ffffff',
  },

  // Warning Color Palette - Vibrant Orange
  warning: {
    0: '#000000',
    10: '#3d2e00',
    20: '#5c4200',
    25: '#6b4d00',
    30: '#7a5900',
    35: '#896500',
    40: '#997100',
    50: '#b8890b',
    60: '#d8a226',
    70: '#f9bc42',
    80: '#ffd95c',
    90: '#fff0c4',
    95: '#fff8e1',
    98: '#fffcf5',
    99: '#fffef7',
    100: '#ffffff',
  },

  // Success Color Palette - Vibrant Green
  success: {
    0: '#000000',
    10: '#002204',
    20: '#003909',
    25: '#00440c',
    30: '#004f0f',
    35: '#005b12',
    40: '#006716',
    50: '#00791e',
    60: '#2d9334',
    70: '#4caf50',
    80: '#68cc6a',
    90: '#86e889',
    95: '#c3f4c4',
    98: '#f0fdf0',
    99: '#f7fef7',
    100: '#ffffff',
  },

  // Neutral Color Palette - Cool Gray with Subtle Blue Tints
  neutral: {
    0: '#000000',
    4: '#0d0f12',
    6: '#12141a',
    10: '#181b21',
    12: '#1d2027',
    17: '#282b33',
    20: '#2f323a',
    22: '#34373f',
    24: '#383b43',
    25: '#3b3e46',
    30: '#464952',
    35: '#52555e',
    40: '#5e616a',
    50: '#767982',
    60: '#8f929b',
    70: '#a9acb5',
    80: '#c5c7cf',
    87: '#dadce3',
    90: '#e3e5eb',
    92: '#e8eaf0',
    94: '#eceef3',
    95: '#f0f2f6',
    96: '#f3f5f8',
    98: '#f8f9fb',
    99: '#fafbfd',
    100: '#ffffff',
  },

  // Neutral Variant Color Palette - Cool Blue-Gray
  neutralVariant: {
    0: '#000000',
    10: '#171b22',
    20: '#2c3038',
    25: '#373b43',
    30: '#42464f',
    35: '#4e525b',
    40: '#5a5e67',
    50: '#72767f',
    60: '#8c9099',
    70: '#a6aab3',
    80: '#c2c5ce',
    90: '#dfe1e9',
    95: '#eef0f6',
    98: '#f7f8fc',
    99: '#fcfdfe',
    100: '#ffffff',
  },
}

// Material Design 3 Theme Creation Function (Light Mode Only)
export const createMaterial3Theme = (): Theme => {
  const isLight = true

  // Light Theme Colors - Cool Gray-Blue Scheme
  const lightColors = {
    primary: material3Colors.primary[40],
    onPrimary: material3Colors.primary[100],
    primaryContainer: material3Colors.primary[90],
    onPrimaryContainer: material3Colors.primary[10],
    
    secondary: material3Colors.secondary[40],
    onSecondary: material3Colors.secondary[100],
    secondaryContainer: material3Colors.secondary[90],
    onSecondaryContainer: material3Colors.secondary[10],
    
    tertiary: material3Colors.tertiary[40],
    onTertiary: material3Colors.tertiary[100],
    tertiaryContainer: material3Colors.tertiary[90],
    onTertiaryContainer: material3Colors.tertiary[10],
    
    error: material3Colors.error[40],
    onError: material3Colors.error[100],
    errorContainer: material3Colors.error[90],
    onErrorContainer: material3Colors.error[10],
    
    warning: material3Colors.warning[40],
    onWarning: material3Colors.warning[100],
    warningContainer: material3Colors.warning[90],
    onWarningContainer: material3Colors.warning[10],
    
    success: material3Colors.success[40],
    onSuccess: material3Colors.success[100],
    successContainer: material3Colors.success[90],
    onSuccessContainer: material3Colors.success[10],
    
    // Cool gray-blue backgrounds (softer than pure white)
    background: '#f5f7fa', // Soft blue-gray background
    onBackground: material3Colors.neutral[10],
    surface: '#f0f3f7', // Slightly cooler surface
    onSurface: material3Colors.neutral[10],
    
    surfaceVariant: '#e8ecf1', // Cool gray-blue variant
    onSurfaceVariant: material3Colors.neutralVariant[30],
    
    // Layered surface containers for depth
    surfaceContainer: '#e3e8ef', // More defined container
    surfaceContainerHigh: '#dde2e9', // Clear elevation
    surfaceContainerHighest: '#d7dce4', // Maximum contrast
    surfaceContainerLow: '#e8ecf1', // Subtle depth
    surfaceContainerLowest: '#f3f5f8', // Minimal elevation
    
    inverseSurface: material3Colors.neutral[20],
    inverseOnSurface: material3Colors.neutral[95],
    inversePrimary: material3Colors.primary[80],
    
    outline: material3Colors.neutralVariant[50],
    outlineVariant: '#c2c5ce', // Softer outline variant
    
    shadow: material3Colors.neutral[0],
    scrim: material3Colors.neutral[0],
    
    surfaceTint: material3Colors.primary[40],
  }

  const colors = lightColors

  // Material Design 3 Elevation System
  const elevations = {
    0: 'none',
    1: isLight 
      ? '0px 1px 2px rgba(0, 0, 0, 0.3), 0px 1px 3px 1px rgba(0, 0, 0, 0.15)'
      : '0px 1px 2px rgba(0, 0, 0, 0.3), 0px 1px 3px 1px rgba(0, 0, 0, 0.15)',
    2: isLight
      ? '0px 1px 2px rgba(0, 0, 0, 0.3), 0px 2px 6px 2px rgba(0, 0, 0, 0.15)'
      : '0px 1px 2px rgba(0, 0, 0, 0.3), 0px 2px 6px 2px rgba(0, 0, 0, 0.15)',
    3: isLight
      ? '0px 1px 3px rgba(0, 0, 0, 0.3), 0px 4px 8px 3px rgba(0, 0, 0, 0.15)'
      : '0px 1px 3px rgba(0, 0, 0, 0.3), 0px 4px 8px 3px rgba(0, 0, 0, 0.15)',
    4: isLight
      ? '0px 2px 3px rgba(0, 0, 0, 0.3), 0px 6px 10px 4px rgba(0, 0, 0, 0.15)'
      : '0px 2px 3px rgba(0, 0, 0, 0.3), 0px 6px 10px 4px rgba(0, 0, 0, 0.15)',
    5: isLight
      ? '0px 4px 4px rgba(0, 0, 0, 0.3), 0px 8px 12px 6px rgba(0, 0, 0, 0.15)'
      : '0px 4px 4px rgba(0, 0, 0, 0.3), 0px 8px 12px 6px rgba(0, 0, 0, 0.15)',
  }

  // Material Design 3 Motion System
  const motion = {
    easing: {
      standard: 'cubic-bezier(0.2, 0.0, 0, 1.0)',
      decelerated: 'cubic-bezier(0.0, 0.0, 0, 1.0)',
      accelerated: 'cubic-bezier(0.3, 0.0, 1.0, 1.0)',
      emphasized: 'cubic-bezier(0.2, 0.0, 0, 1.0)',
      emphasizedDecelerate: 'cubic-bezier(0.05, 0.7, 0.1, 1.0)',
      emphasizedAccelerate: 'cubic-bezier(0.3, 0.0, 0.8, 0.15)',
      legacy: 'cubic-bezier(0.4, 0.0, 0.2, 1.0)',
    },
    duration: {
      short1: 50,
      short2: 100,
      short3: 150,
      short4: 200,
      medium1: 250,
      medium2: 300,
      medium3: 350,
      medium4: 400,
      long1: 450,
      long2: 500,
      long3: 550,
      long4: 600,
      extraLong1: 700,
      extraLong2: 800,
      extraLong3: 900,
      extraLong4: 1000,
    },
  }

  const baseTheme = createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: colors.primary,
        light: isLight ? material3Colors.primary[80] : material3Colors.primary[60],
        dark: isLight ? material3Colors.primary[20] : material3Colors.primary[30],
        contrastText: colors.onPrimary,
      },
      secondary: {
        main: colors.secondary,
        light: isLight ? material3Colors.secondary[80] : material3Colors.secondary[60],
        dark: isLight ? material3Colors.secondary[20] : material3Colors.secondary[30],
        contrastText: colors.onSecondary,
      },
      tertiary: {
        main: colors.tertiary,
        light: isLight ? material3Colors.tertiary[80] : material3Colors.tertiary[60],
        dark: isLight ? material3Colors.tertiary[20] : material3Colors.tertiary[30],
        contrastText: colors.onTertiary,
      },
      error: {
        main: colors.error,
        light: isLight ? material3Colors.error[80] : material3Colors.error[60],
        dark: isLight ? material3Colors.error[20] : material3Colors.error[30],
        contrastText: colors.onError,
      },
      warning: {
        main: colors.warning,
        light: isLight ? material3Colors.warning[80] : material3Colors.warning[60],
        dark: isLight ? material3Colors.warning[20] : material3Colors.warning[30],
        contrastText: colors.onWarning,
      },
      info: {
        main: colors.primary,
        light: isLight ? material3Colors.primary[80] : material3Colors.primary[60],
        dark: isLight ? material3Colors.primary[20] : material3Colors.primary[30],
        contrastText: colors.onPrimary,
      },
      success: {
        main: colors.success,
        light: isLight ? material3Colors.success[80] : material3Colors.success[60],
        dark: isLight ? material3Colors.success[20] : material3Colors.success[30],
        contrastText: colors.onSuccess,
      },
      background: {
        default: colors.background,
        paper: colors.surface,
      },
      text: {
        primary: colors.onSurface,
        secondary: colors.onSurfaceVariant,
        disabled: isLight ? material3Colors.neutral[40] : material3Colors.neutral[60],
      },
      divider: colors.outlineVariant,
      action: {
        active: colors.onSurface,
        hover: isLight 
          ? `${colors.onSurface}08` 
          : `${colors.onSurface}0A`,
        selected: isLight 
          ? `${colors.onSurface}12` 
          : `${colors.onSurface}14`,
        disabled: isLight ? material3Colors.neutral[40] : material3Colors.neutral[60],
        disabledBackground: isLight 
          ? `${colors.onSurface}12` 
          : `${colors.onSurface}12`,
        focus: isLight 
          ? `${colors.onSurface}12` 
          : `${colors.onSurface}12`,
      },
      // Custom Material Design 3 colors
      surfaceVariant: colors.surfaceVariant,
      onSurfaceVariant: colors.onSurfaceVariant,
      outline: colors.outline,
      outlineVariant: colors.outlineVariant,
      surfaceContainer: colors.surfaceContainer,
      surfaceContainerHigh: colors.surfaceContainerHigh,
      surfaceContainerHighest: colors.surfaceContainerHighest,
      surfaceContainerLow: colors.surfaceContainerLow,
      surfaceContainerLowest: colors.surfaceContainerLowest,
      inverseSurface: colors.inverseSurface,
      inverseOnSurface: colors.inverseOnSurface,
      inversePrimary: colors.inversePrimary,
      shadow: colors.shadow,
      scrim: colors.scrim,
      surfaceTint: colors.surfaceTint,
      primaryContainer: colors.primaryContainer,
      onPrimaryContainer: colors.onPrimaryContainer,
      secondaryContainer: colors.secondaryContainer,
      onSecondaryContainer: colors.onSecondaryContainer,
      tertiaryContainer: colors.tertiaryContainer,
      onTertiaryContainer: colors.onTertiaryContainer,
      errorContainer: colors.errorContainer,
      onErrorContainer: colors.onErrorContainer,
      warningContainer: colors.warningContainer,
      onWarningContainer: colors.onWarningContainer,
      successContainer: colors.successContainer,
      onSuccessContainer: colors.onSuccessContainer,
    } as any,

    // Material Design 3 Typography Scale
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      
      // Display styles
      displayLarge: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 400,
        fontSize: '3.5625rem', // 57px
        lineHeight: '4rem', // 64px
        letterSpacing: '-0.25px',
      },
      displayMedium: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 400,
        fontSize: '2.8125rem', // 45px
        lineHeight: '3.25rem', // 52px
        letterSpacing: '0px',
      },
      displaySmall: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 400,
        fontSize: '2.25rem', // 36px
        lineHeight: '2.75rem', // 44px
        letterSpacing: '0px',
      },
      
      // Headline styles
      headlineLarge: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 400,
        fontSize: '2rem', // 32px
        lineHeight: '2.5rem', // 40px
        letterSpacing: '0px',
      },
      headlineMedium: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 400,
        fontSize: '1.75rem', // 28px
        lineHeight: '2.25rem', // 36px
        letterSpacing: '0px',
      },
      headlineSmall: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 400,
        fontSize: '1.5rem', // 24px
        lineHeight: '2rem', // 32px
        letterSpacing: '0px',
      },
      
      // Title styles
      titleLarge: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 400,
        fontSize: '1.375rem', // 22px
        lineHeight: '1.75rem', // 28px
        letterSpacing: '0px',
      },
      titleMedium: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 500,
        fontSize: '1rem', // 16px
        lineHeight: '1.5rem', // 24px
        letterSpacing: '0.15px',
      },
      titleSmall: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 500,
        fontSize: '0.875rem', // 14px
        lineHeight: '1.25rem', // 20px
        letterSpacing: '0.1px',
      },
      
      // Label styles
      labelLarge: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 500,
        fontSize: '0.875rem', // 14px
        lineHeight: '1.25rem', // 20px
        letterSpacing: '0.1px',
      },
      labelMedium: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 500,
        fontSize: '0.75rem', // 12px
        lineHeight: '1rem', // 16px
        letterSpacing: '0.5px',
      },
      labelSmall: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 500,
        fontSize: '0.6875rem', // 11px
        lineHeight: '1rem', // 16px
        letterSpacing: '0.5px',
      },
      
      // Body styles
      bodyLarge: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 400,
        fontSize: '1rem', // 16px
        lineHeight: '1.5rem', // 24px
        letterSpacing: '0.5px',
      },
      bodyMedium: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 400,
        fontSize: '0.875rem', // 14px
        lineHeight: '1.25rem', // 20px
        letterSpacing: '0.25px',
      },
      bodySmall: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 400,
        fontSize: '0.75rem', // 12px
        lineHeight: '1rem', // 16px
        letterSpacing: '0.4px',
      },

      // Legacy MUI typography mapping
      h1: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 400,
        fontSize: '2rem', // 32px - headlineLarge
        lineHeight: '2.5rem', // 40px
        letterSpacing: '0px',
      },
      h2: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 400,
        fontSize: '1.75rem', // 28px - headlineMedium
        lineHeight: '2.25rem', // 36px
        letterSpacing: '0px',
      },
      h3: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 400,
        fontSize: '1.5rem', // 24px - headlineSmall
        lineHeight: '2rem', // 32px
        letterSpacing: '0px',
      },
      h4: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 400,
        fontSize: '1.375rem', // 22px - titleLarge
        lineHeight: '1.75rem', // 28px
        letterSpacing: '0px',
      },
      h5: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 500,
        fontSize: '1rem', // 16px - titleMedium
        lineHeight: '1.5rem', // 24px
        letterSpacing: '0.15px',
      },
      h6: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 500,
        fontSize: '0.875rem', // 14px - titleSmall
        lineHeight: '1.25rem', // 20px
        letterSpacing: '0.1px',
      },
      subtitle1: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 500,
        fontSize: '1rem', // 16px - titleMedium
        lineHeight: '1.5rem', // 24px
        letterSpacing: '0.15px',
      },
      subtitle2: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 500,
        fontSize: '0.875rem', // 14px - titleSmall
        lineHeight: '1.25rem', // 20px
        letterSpacing: '0.1px',
      },
      body1: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 400,
        fontSize: '1rem', // 16px - bodyLarge
        lineHeight: '1.5rem', // 24px
        letterSpacing: '0.5px',
      },
      body2: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 400,
        fontSize: '0.875rem', // 14px - bodyMedium
        lineHeight: '1.25rem', // 20px
        letterSpacing: '0.25px',
      },
      button: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 500,
        fontSize: '0.875rem', // 14px - labelLarge
        lineHeight: '1.25rem', // 20px
        letterSpacing: '0.1px',
        textTransform: 'none' as const,
      },
      caption: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 400,
        fontSize: '0.75rem', // 12px - bodySmall
        lineHeight: '1rem', // 16px
        letterSpacing: '0.4px',
      },
      overline: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontWeight: 500,
        fontSize: '0.6875rem', // 11px - labelSmall
        lineHeight: '1rem', // 16px
        letterSpacing: '0.5px',
        textTransform: 'uppercase' as const,
      },
    } as any,

    // Material Design 3 Shape System
    shape: {
      borderRadius: 12, // Medium corner radius
    },

    // Material Design 3 Spacing System (8px base unit)
    spacing: 8,

    // Material Design 3 Shadows
    shadows: [
      elevations[0],
      elevations[1],
      elevations[2],
      elevations[3],
      elevations[4],
      elevations[5],
      elevations[5], // 6
      elevations[5], // 7
      elevations[5], // 8
      elevations[5], // 9
      elevations[5], // 10
      elevations[5], // 11
      elevations[5], // 12
      elevations[5], // 13
      elevations[5], // 14
      elevations[5], // 15
      elevations[5], // 16
      elevations[5], // 17
      elevations[5], // 18
      elevations[5], // 19
      elevations[5], // 20
      elevations[5], // 21
      elevations[5], // 22
      elevations[5], // 23
      elevations[5], // 24
    ] as any,

    // Material Design 3 Transitions
    transitions: {
      easing: {
        easeInOut: motion.easing.standard,
        easeOut: motion.easing.decelerated,
        easeIn: motion.easing.accelerated,
        sharp: motion.easing.emphasized,
      },
      duration: {
        shortest: motion.duration.short1,
        shorter: motion.duration.short2,
        short: motion.duration.short3,
        standard: motion.duration.medium2,
        complex: motion.duration.medium4,
        enteringScreen: motion.duration.medium3,
        leavingScreen: motion.duration.short4,
      },
    },
  })

  // Material Design 3 Component Overrides
  return createTheme(baseTheme, {
    components: {
      // Button Components
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 20, // Full corner radius for buttons
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            lineHeight: '1.25rem',
            letterSpacing: '0.1px',
            minHeight: 40,
            paddingLeft: 24,
            paddingRight: 24,
            paddingTop: 10,
            paddingBottom: 10,
            transition: `all ${motion.duration.short2}ms ${motion.easing.standard}`,
            '&:hover': {
              boxShadow: elevations[1],
            },
          },
          contained: {
            backgroundColor: colors.primary,
            color: colors.onPrimary,
            boxShadow: elevations[0],
            '&:hover': {
              backgroundColor: isLight ? material3Colors.primary[35] : material3Colors.primary[70],
              boxShadow: elevations[1],
            },
            '&:active': {
              boxShadow: elevations[0],
            },
            '&:disabled': {
              backgroundColor: isLight 
                ? `${colors.onSurface}12` 
                : `${colors.onSurface}12`,
              color: isLight ? material3Colors.neutral[40] : material3Colors.neutral[60],
            },
          },
          outlined: {
            backgroundColor: 'transparent',
            color: colors.primary,
            borderColor: colors.outline,
            borderWidth: 1,
            '&:hover': {
              backgroundColor: `${colors.primary}08`,
              borderColor: colors.primary,
            },
            '&:disabled': {
              borderColor: isLight 
                ? `${colors.onSurface}12` 
                : `${colors.onSurface}12`,
              color: isLight ? material3Colors.neutral[40] : material3Colors.neutral[60],
            },
          },
          text: {
            backgroundColor: 'transparent',
            color: colors.primary,
            '&:hover': {
              backgroundColor: `${colors.primary}08`,
            },
            '&:disabled': {
              color: isLight ? material3Colors.neutral[40] : material3Colors.neutral[60],
            },
          },
        },
      },

      // Floating Action Button
      MuiFab: {
        styleOverrides: {
          root: {
            backgroundColor: colors.primaryContainer,
            color: colors.onPrimaryContainer,
            boxShadow: elevations[3],
            '&:hover': {
              backgroundColor: isLight 
                ? material3Colors.primary[80] 
                : material3Colors.primary[85],
              boxShadow: elevations[4],
            },
            '&:active': {
              boxShadow: elevations[3],
            },
          },
          primary: {
            backgroundColor: colors.primary,
            color: colors.onPrimary,
            '&:hover': {
              backgroundColor: isLight ? material3Colors.primary[35] : material3Colors.primary[70],
            },
          },
          secondary: {
            backgroundColor: colors.secondary,
            color: colors.onSecondary,
            '&:hover': {
              backgroundColor: isLight ? material3Colors.secondary[35] : material3Colors.secondary[70],
            },
          },
        },
      },

      // Card Component
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: colors.surfaceContainerLow,
            borderRadius: 12,
            boxShadow: elevations[1],
            border: 'none',
            transition: `all ${motion.duration.short3}ms ${motion.easing.standard}`,
            '&:hover': {
              boxShadow: elevations[2],
            },
          },
        },
      },

      // Paper Component
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: colors.surface,
            borderRadius: 12,
          },
          elevation0: {
            backgroundColor: colors.surface,
            boxShadow: elevations[0],
          },
          elevation1: {
            backgroundColor: colors.surfaceContainer,
            boxShadow: elevations[1],
          },
          elevation2: {
            backgroundColor: colors.surfaceContainerHigh,
            boxShadow: elevations[2],
          },
          elevation3: {
            backgroundColor: colors.surfaceContainerHigh,
            boxShadow: elevations[3],
          },
          elevation4: {
            backgroundColor: colors.surfaceContainerHighest,
            boxShadow: elevations[4],
          },
          elevation5: {
            backgroundColor: colors.surfaceContainerHighest,
            boxShadow: elevations[5],
          },
        },
      },

      // AppBar Component
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: colors.surface,
            color: colors.onSurface,
            boxShadow: elevations[2],
            borderBottom: 'none',
          },
        },
      },

      // Drawer Component
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.surfaceContainerLow,
            borderRight: 'none',
            boxShadow: elevations[1],
          },
        },
      },

      // TextField Component
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: colors.surfaceContainerHighest,
              borderRadius: 4, // Small corner radius for text fields
              '& fieldset': {
                borderColor: colors.outline,
                borderWidth: 1,
              },
              '&:hover fieldset': {
                borderColor: colors.onSurfaceVariant,
              },
              '&.Mui-focused fieldset': {
                borderColor: colors.primary,
                borderWidth: 2,
              },
              '&.Mui-error fieldset': {
                borderColor: colors.error,
              },
            },
            '& .MuiInputLabel-root': {
              color: colors.onSurfaceVariant,
              '&.Mui-focused': {
                color: colors.primary,
              },
              '&.Mui-error': {
                color: colors.error,
              },
            },
            '& .MuiOutlinedInput-input': {
              color: colors.onSurface,
            },
          },
        },
      },

      // Chip Component
      MuiChip: {
        styleOverrides: {
          root: {
            backgroundColor: colors.surfaceContainerHigh,
            color: colors.onSurfaceVariant,
            borderRadius: 8,
            border: `1px solid ${colors.outline}`,
            '&:hover': {
              backgroundColor: colors.surfaceContainerHighest,
            },
          },
          filled: {
            backgroundColor: colors.secondaryContainer,
            color: colors.onSecondaryContainer,
            border: 'none',
          },
          outlined: {
            backgroundColor: 'transparent',
            borderColor: colors.outline,
          },
        },
      },

      // Dialog Component
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.surfaceContainerHigh,
            borderRadius: 28, // Extra large corner radius for dialogs
            boxShadow: elevations[3],
          },
        },
      },

      // Menu Component
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.surfaceContainer,
            borderRadius: 4,
            boxShadow: elevations[2],
            border: 'none',
          },
        },
      },

      // Tooltip Component
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: colors.inverseSurface,
            color: colors.inverseOnSurface,
            borderRadius: 4,
            fontSize: '0.75rem',
            fontWeight: 500,
          },
        },
      },

      // List Components
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            margin: '4px 8px',
            '&:hover': {
              backgroundColor: `${colors.onSurface}08`,
            },
            '&.Mui-selected': {
              backgroundColor: colors.secondaryContainer,
              color: colors.onSecondaryContainer,
              '&:hover': {
                backgroundColor: colors.secondaryContainer,
              },
            },
          },
        },
      },

      // Table Components
      MuiTableContainer: {
        styleOverrides: {
          root: {
            backgroundColor: colors.surfaceContainer,
            borderRadius: 12,
            boxShadow: elevations[1],
            border: 'none',
          },
        },
      },

      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: colors.surfaceContainerHigh,
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${colors.outlineVariant}`,
            color: colors.onSurface,
          },
          head: {
            fontWeight: 500,
            color: colors.onSurfaceVariant,
            backgroundColor: colors.surfaceContainerHigh,
          },
        },
      },

      // Icon Button Component
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            transition: `all ${motion.duration.short2}ms ${motion.easing.standard}`,
            '&:hover': {
              backgroundColor: `${colors.onSurface}08`,
            },
          },
        },
      },

      // Switch Component
      MuiSwitch: {
        styleOverrides: {
          root: {
            '& .MuiSwitch-switchBase': {
              '&.Mui-checked': {
                color: colors.onPrimary,
                '& + .MuiSwitch-track': {
                  backgroundColor: colors.primary,
                },
              },
            },
            '& .MuiSwitch-track': {
              backgroundColor: colors.surfaceContainerHighest,
              border: `1px solid ${colors.outline}`,
            },
          },
        },
      },

      // Checkbox Component
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: colors.onSurfaceVariant,
            '&.Mui-checked': {
              color: colors.primary,
            },
          },
        },
      },

      // Radio Component
      MuiRadio: {
        styleOverrides: {
          root: {
            color: colors.onSurfaceVariant,
            '&.Mui-checked': {
              color: colors.primary,
            },
          },
        },
      },

      // Slider Component
      MuiSlider: {
        styleOverrides: {
          root: {
            color: colors.primary,
            '& .MuiSlider-track': {
              backgroundColor: colors.primary,
            },
            '& .MuiSlider-rail': {
              backgroundColor: colors.surfaceContainerHighest,
            },
            '& .MuiSlider-thumb': {
              backgroundColor: colors.primary,
              boxShadow: elevations[1],
            },
          },
        },
      },

      // Progress Components
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            backgroundColor: colors.surfaceContainerHighest,
            borderRadius: 2,
            '& .MuiLinearProgress-bar': {
              backgroundColor: colors.primary,
              borderRadius: 2,
            },
          },
        },
      },

      MuiCircularProgress: {
        styleOverrides: {
          root: {
            color: colors.primary,
          },
        },
      },

      // Stepper Components
      MuiStepLabel: {
        styleOverrides: {
          root: {
            '& .MuiStepLabel-label': {
              color: colors.onSurfaceVariant,
              '&.Mui-active': {
                color: colors.onSurface,
                fontWeight: 500,
              },
              '&.Mui-completed': {
                color: colors.onSurfaceVariant,
              },
            },
          },
        },
      },

      MuiStepIcon: {
        styleOverrides: {
          root: {
            color: colors.surfaceContainerHighest,
            border: `2px solid ${colors.outline}`,
            borderRadius: '50%',
            '&.Mui-active': {
              color: colors.primary,
              border: 'none',
            },
            '&.Mui-completed': {
              color: colors.primary,
              border: 'none',
            },
          },
        },
      },

      // Alert Component
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: 'none',
          },
          standardSuccess: {
            backgroundColor: colors.successContainer,
            color: colors.onSuccessContainer,
          },
          standardInfo: {
            backgroundColor: colors.primaryContainer,
            color: colors.onPrimaryContainer,
          },
          standardWarning: {
            backgroundColor: colors.warningContainer,
            color: colors.onWarningContainer,
          },
          standardError: {
            backgroundColor: colors.errorContainer,
            color: colors.onErrorContainer,
          },
        },
      },

      // Badge Component
      MuiBadge: {
        styleOverrides: {
          badge: {
            backgroundColor: colors.error,
            color: colors.onError,
            fontWeight: 500,
            fontSize: '0.6875rem',
            minWidth: 16,
            height: 16,
          },
        },
      },

      // Accordion Components
      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundColor: colors.surfaceContainer,
            borderRadius: 12,
            boxShadow: elevations[1],
            border: 'none',
            '&:before': {
              display: 'none',
            },
            '&.Mui-expanded': {
              margin: 0,
            },
          },
        },
      },

      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            backgroundColor: colors.surfaceContainer,
            borderRadius: '12px 12px 0 0',
            '&.Mui-expanded': {
              borderRadius: '12px 12px 0 0',
            },
          },
        },
      },

      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            backgroundColor: colors.surfaceContainerLow,
            borderRadius: '0 0 12px 12px',
          },
        },
      },
    },
  })
}

// Default Material Design 3 light theme
export const material3LightTheme = createMaterial3Theme()

// Utility functions for Material Design 3 styling
export const createMaterial3Surface = (elevation: 0 | 1 | 2 | 3 | 4 | 5 = 1) => {
  const colors = createMaterial3Theme().palette
  
  const elevations = {
    0: 'none',
    1: '0px 1px 2px rgba(0, 0, 0, 0.3), 0px 1px 3px 1px rgba(0, 0, 0, 0.15)',
    2: '0px 1px 2px rgba(0, 0, 0, 0.3), 0px 2px 6px 2px rgba(0, 0, 0, 0.15)',
    3: '0px 1px 3px rgba(0, 0, 0, 0.3), 0px 4px 8px 3px rgba(0, 0, 0, 0.15)',
    4: '0px 2px 3px rgba(0, 0, 0, 0.3), 0px 6px 10px 4px rgba(0, 0, 0, 0.15)',
    5: '0px 4px 4px rgba(0, 0, 0, 0.3), 0px 8px 12px 6px rgba(0, 0, 0, 0.15)',
  }

  const surfaceColors: Record<number, string> = {
    0: (colors as any).surface || '#f0f3f7',
    1: (colors as any).surfaceContainer || '#e3e8ef',
    2: (colors as any).surfaceContainerHigh || '#dde2e9',
    3: (colors as any).surfaceContainerHigh || '#dde2e9',
    4: (colors as any).surfaceContainerHighest || '#d7dce4',
    5: (colors as any).surfaceContainerHighest || '#d7dce4',
  }

  return {
    backgroundColor: surfaceColors[elevation],
    boxShadow: elevations[elevation],
    borderRadius: 12,
    border: 'none',
  }
}

export const createMaterial3StateLayer = (color: string, opacity: number) => {
  return `${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`
}

export default material3LightTheme
